import React, { useState, useMemo, useEffect } from 'react';
import './tablecomponent.scss';
import { getWeeklyMaster, fetchAccuracyReport } from '../../services/forecastService';
import { displayProductLabel } from '../../utils/demoProductLabels.js';
import ForecastTrendChart from '../ForecastTrendChart';

// ─────────────────────────────────────────────────────────────────────────────
// Accuracy helpers⬇️  (MAPE-based, clamped to [0, 100])
// Returns null when actual = 0 to avoid division by zero.
// ─────────────────────────────────────────────────────────────────────────────
function calculateAccuracy(actual, forecast) {
  const a = Number(actual);
  const f = Number(forecast);
  if (!a) return null;
  const mape = (Math.abs(a - f) / a) * 100;
  return Math.max(0, Math.min(100, 100 - mape));
}

function fmtAcc(val) {
  if (val === null || val === undefined) return '—';
  return `${val.toFixed(1)}%`;
}

// Colour-coded accuracy cell
function AccCell({ actual, forecast }) {
  const acc = calculateAccuracy(actual, forecast);
  let cls = 'acc-null';
  if (acc !== null) {
    if (acc >= 80) cls = 'acc-high';
    else if (acc >= 50) cls = 'acc-mid';
    else cls = 'acc-low';
  }
  return <td className={`acc-cell ${cls} border-b border-b-[#d6d6d6]`}>{fmtAcc(acc)}</td>;
}

// ─────────────────────────────────────────────────────────────────────────────

export default function FcstAccuracy({ selectedItemName, selectedType, sopData, isSingleModel }) {

  const [overlayLoading, setOverlayLoading] = useState(false);
  const [displayWeeks, setDisplayWeeks] = useState([]);
  const [accuracyData, setAccuracyData] = useState({});

  // ── Model list — mirrors SOPTable sopSelectedItem ─────────────────────────
  const sopSelectedItem = useMemo(() => {
    if (!selectedItemName) return [];
    if (isSingleModel) return [{ models: [selectedItemName] }];
    return sopData?.[selectedItemName] || [];
  }, [selectedItemName, sopData, isSingleModel]);

  // ── Phase 2: last-12-weeks from week_master ───────
  useEffect(() => {
    async function fetchMaster() {
      try {
        const result = await getWeeklyMaster();

        const today = new Date();
        const formattedToday = today
          .toLocaleString('en-GB', { day: '2-digit', month: 'short' })
          .replace(' ', '-')
          .replace('.', '');

        const currentWeekRow = result.find((r) => r.dates === formattedToday);
        if (!currentWeekRow) {
          console.warn('FcstAccuracy: no week found for', formattedToday);
          setDisplayWeeks([]);
          return;
        }

        // Build weekInfo using last_day flag — exact mirror of SOPTable
        const weekInfo = {};
        result.forEach((row) => {
          const weekNo = Number(row.wk_no);
          const [day, month] = row.dates.split('-');
          if (!weekInfo[weekNo]) {
            weekInfo[weekNo] = { week_name: row.week_name, month, start: day, end: day };
          }
          if (row.last_day === 1) weekInfo[weekNo].end = day;
        });

        const uniqueWeeks = [...new Set(result.map((r) => r.wk_no))].sort((a, b) => a - b);
        const totalWeeks = uniqueWeeks.length;
        const currentIndex = uniqueWeeks.findIndex((wk) => Number(wk) === Number(currentWeekRow.wk_no));

        // Traverse backward 12 weeks (wrap cyclically for year-end edge case)
        const last12 = [];
        for (let i = 1; i <= 12; i++) {
          let idx = currentIndex - i;
          if (idx < 0) idx = totalWeeks + idx;
          last12.unshift(uniqueWeeks[idx]);
        }

        const weeks = last12.map((weekNo) => {
          const info = weekInfo[weekNo] || {};
          const range = info.start && info.end ? `${info.start} – ${info.end}` : '';
          return {
            wk_no: weekNo,
            week_name: `${info.week_name || `Wk ${weekNo}`} - ${info.month || ''}`,
            range,
          };
        });

        setDisplayWeeks(weeks);
      } catch (err) {
        console.error('FcstAccuracy: fetchMaster error', err);
      }
    }
    fetchMaster();
  }, []);

  // ── Phase 3: Fetch accuracy report from backend ───────────────────────────
  useEffect(() => {
    async function load() {
      try {
        setOverlayLoading(true);
        const resp = await fetchAccuracyReport();
        // resp = { last12_weeks: [...], data: { model_code: { primary|secondary|tertiary: { statistical, cons, mlr, xgb, actuals } } } }
        setAccuracyData(resp.data || {});
      } catch (err) {
        console.error('FcstAccuracy: fetchAccuracyReport error', err);
      } finally {
        setOverlayLoading(false);
      }
    }
    load();
  }, []);

  // ── Derived ───────────────────────────────────────────────────────────────
  const displayWeekNos = useMemo(() => displayWeeks.map((w) => w.wk_no), [displayWeeks]);

  // Single cell value
  const getVal = (modelCode, metric, weekNo) =>
    Number(accuracyData?.[modelCode]?.[selectedType]?.[metric]?.[weekNo] ?? 0);

  // Row total (data rows)
  const getTotal = (modelCode, metric) =>
    displayWeekNos.reduce((sum, wk) => sum + getVal(modelCode, metric, wk), 0);

  // Average accuracy across all display weeks (ignores null / zero-actual weeks)
  const getAvgAccuracy = (modelCode, forecastMetric) => {
    const values = displayWeekNos
      .map((wk) => calculateAccuracy(getVal(modelCode, 'actuals', wk), getVal(modelCode, forecastMetric, wk)))
      .filter((v) => v !== null);
    if (!values.length) return null;
    return values.reduce((a, b) => a + b, 0) / values.length;
  };

  const accuracyTrendChart = useMemo(() => {
    const modelCodes = sopSelectedItem.flatMap((group) => group?.models || []);
    if (!modelCodes.length || !displayWeekNos.length) return null;

    const aggregateMetric = (metricKey, weekNo) =>
      modelCodes.reduce(
        (sum, modelCode) => sum + (Number(accuracyData?.[modelCode]?.[selectedType]?.[metricKey]?.[weekNo]) || 0),
        0
      );

    const buildAccuracyValues = (metricKey) =>
      displayWeekNos.map((weekNo) => {
        const actuals = aggregateMetric('actuals', weekNo);
        const forecast = aggregateMetric(metricKey, weekNo);
        const accuracy = calculateAccuracy(actuals, forecast);
        return accuracy ?? 0;
      });

    return {
      labels: displayWeekNos.map((wkNo) => `Wk ${wkNo}`),
      series: [
        { key: 'stat', label: 'Statistical Acc', color: '#002FA7', values: buildAccuracyValues('statistical') },
        { key: 'cons', label: 'Consensus Acc', color: '#16A34A', values: buildAccuracyValues('cons') },
        { key: 'mlrf', label: 'MLRF Acc', color: '#9333EA', values: buildAccuracyValues('mlr') },
        { key: 'xgbf', label: 'XGBF Acc', color: '#EA580C', values: buildAccuracyValues('xgb') },
      ],
    };
  }, [sopSelectedItem, displayWeekNos, accuracyData, selectedType]);

  // Row colour cycling — matches SOPTable
  const colorArr = ['blue', 'yellow', 'pink', 'green'];
  let counter = 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="tableUI h-full w-full table-container">
      {overlayLoading && (
        <div className="table-loader-overlay">
          <div className="loader-content">
            <div className="spinner"></div>
            <p>Loading accuracy data...</p>
          </div>
        </div>
      )}

      <table className="table table-bordered table-hover table-striped custome-talbe _sand-op_table border-2">

        {/* ── HEADER ── */}
        <thead className="fix [&>tr>th]:sticky [&>tr>th]:top-0 [&>tr>th]:bg-white [&>tr>th]:z-20">
          <tr>
            <th className="sticky-col sticky-model border-r-2 border-r-[#0000004f]">Model</th>
            <th className="sticky-col sticky-measure border-r-2 border-r-[#0000004f]">Measure</th>
            {displayWeeks.map((wk) => (
              <th key={wk.wk_no} className="border-right">
                <div className="hCol">
                  <div>{wk.week_name}</div>
                  {wk.range && <div className="font-extralight">{wk.range}</div>}
                </div>
              </th>
            ))}
            <th className="grand-total-header">Total / Avg</th>
          </tr>
        </thead>

        {/* ── BODY ── */}
        <tbody>

          {sopSelectedItem.length === 0 && (
            <tr>
              <td colSpan={999}>
                <div className="empty-state">
                  <h3>No Data Selected</h3>
                  <p>Select a Category or Model from the left panel to view forecast accuracy.</p>
                </div>
              </td>
            </tr>
          )}

          {sopSelectedItem.map((ele, index) => (
            <React.Fragment key={index}>
              {ele?.models?.map((modelCode, i) => {
                counter++;
                if (counter > 3) counter = 0;
                const color = colorArr[counter];

                return (
                  <React.Fragment key={i}>

                    {/* Row label — rowSpan covers all 9 data + accuracy rows */}
                    <tr className={color}>
                      <td rowSpan={10} className="border-rft sticky-col sticky-model">{displayProductLabel(modelCode)}</td>
                    </tr>

                    {/* ── DATA ROWS ── */}

                    {/* Statistical */}
                    <tr className={color}>
                      <td className="border-r border-r-[#0000004f] sticky-col sticky-measure">Statistical</td>
                      {displayWeekNos.map((wk) => (
                        <td key={wk} className="border-b border-b-[#d6d6d6]">
                          {getVal(modelCode, 'statistical', wk).toFixed(0)}
                        </td>
                      ))}
                      <td className="font-semibold grand-total-cell">
                        {getTotal(modelCode, 'statistical').toFixed(0)}
                      </td>
                    </tr>

                    {/* Consensus */}
                    <tr className={color}>
                      <td className="border-r border-r-[#0000004f] sticky-col sticky-measure">Consensus</td>
                      {displayWeekNos.map((wk) => (
                        <td key={wk} className="border-b border-b-[#d6d6d6]">
                          {getVal(modelCode, 'cons', wk).toFixed(0)}
                        </td>
                      ))}
                      <td className="font-semibold grand-total-cell">
                        {getTotal(modelCode, 'cons').toFixed(0)}
                      </td>
                    </tr>

                    {/* MLRF */}
                    <tr className={color}>
                      <td title="Multiple Linear Regression Forecasting" className="border-r border-r-[#0000004f] sticky-col sticky-measure">MLRF</td>
                      {displayWeekNos.map((wk) => (
                        <td key={wk} className="border-b border-b-[#d6d6d6]">
                          {getVal(modelCode, 'mlr', wk).toFixed(0)}
                        </td>
                      ))}
                      <td className="font-semibold grand-total-cell">
                        {getTotal(modelCode, 'mlr').toFixed(0)}
                      </td>
                    </tr>

                    {/* XGBF */}
                    <tr className={color}>
                      <td title="XGBoost Regressor Forecasting" className="border-r border-r-[#0000004f] sticky-col sticky-measure">XGBF</td>
                      {displayWeekNos.map((wk) => (
                        <td key={wk} className="border-b border-b-[#d6d6d6]">
                          {getVal(modelCode, 'xgb', wk).toFixed(0)}
                        </td>
                      ))}
                      <td className="font-semibold grand-total-cell">
                        {getTotal(modelCode, 'xgb').toFixed(0)}
                      </td>
                    </tr>

                    {/* Actuals */}
                    <tr className={color}>
                      <td className="border-r border-r-[#0000004f] sticky-col sticky-measure font-semibold">Actuals</td>
                      {displayWeekNos.map((wk) => (
                        <td key={wk} className="border-b border-b-[#d6d6d6] font-semibold">
                          {getVal(modelCode, 'actuals', wk).toFixed(0)}
                        </td>
                      ))}
                      <td className="font-semibold grand-total-cell">
                        {getTotal(modelCode, 'actuals').toFixed(0)}
                      </td>
                    </tr>

                    {/* ── ACCURACY ROWS ── */}

                    {/* Acc vs Statistical */}
                    <tr className={color}>
                      <td className="border-r border-r-[#0000004f] sticky-col sticky-measure">Acc vs Stat</td>
                      {displayWeekNos.map((wk) => (
                        <AccCell
                          key={wk}
                          actual={getVal(modelCode, 'actuals', wk)}
                          forecast={getVal(modelCode, 'statistical', wk)}
                        />
                      ))}
                      <td className="font-semibold grand-total-cell">
                        {fmtAcc(getAvgAccuracy(modelCode, 'statistical'))}
                      </td>
                    </tr>

                    {/* Acc vs Consensus */}
                    <tr className={color}>
                      <td className="border-r border-r-[#0000004f] sticky-col sticky-measure">Acc vs Cons</td>
                      {displayWeekNos.map((wk) => (
                        <AccCell
                          key={wk}
                          actual={getVal(modelCode, 'actuals', wk)}
                          forecast={getVal(modelCode, 'cons', wk)}
                        />
                      ))}
                      <td className="font-semibold grand-total-cell">
                        {fmtAcc(getAvgAccuracy(modelCode, 'cons'))}
                      </td>
                    </tr>

                    {/* Acc vs MLRF */}
                    <tr className={color}>
                      <td className="border-r border-r-[#0000004f] sticky-col sticky-measure">Acc vs MLRF</td>
                      {displayWeekNos.map((wk) => (
                        <AccCell
                          key={wk}
                          actual={getVal(modelCode, 'actuals', wk)}
                          forecast={getVal(modelCode, 'mlr', wk)}
                        />
                      ))}
                      <td className="font-semibold grand-total-cell">
                        {fmtAcc(getAvgAccuracy(modelCode, 'mlr'))}
                      </td>
                    </tr>

                    {/* Acc vs XGBF */}
                    <tr className={`${color} border-b-2 border-b-[#0000004f]`}>
                      <td className="border-r border-r-[#0000004f] sticky-col sticky-measure">Acc vs XGBF</td>
                      {displayWeekNos.map((wk) => (
                        <AccCell
                          key={wk}
                          actual={getVal(modelCode, 'actuals', wk)}
                          forecast={getVal(modelCode, 'xgb', wk)}
                        />
                      ))}
                      <td className="font-semibold grand-total-cell">
                        {fmtAcc(getAvgAccuracy(modelCode, 'xgb'))}
                      </td>
                    </tr>

                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {accuracyTrendChart && (
        <ForecastTrendChart
          title="Weekly Forecast Accuracy Trend"
          subtitle={`${selectedType?.toUpperCase() || "TERTIARY"} weighted accuracy across selected models`}
          labels={accuracyTrendChart.labels}
          series={accuracyTrendChart.series}
          isPercent
        />
      )}
    </div>
  );
}
